/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { useEffect, useMemo, useState } from "react";
import {
  documentsUpload,
  erpSku,
  retailers,
  sku_mapping,
} from "@/services/global.services";
import { useToast } from "@/hooks/use-toast";
import { getFileUrl } from "@/lib/get-file-url";

export const useMutations = () => {
  const API_SERVICE = sku_mapping;
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);
  const [retailerList, setRetailerList] = useState([]);
  const [retailerDropdownList, setRetailerDropdownList] = useState([]);
  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [companyData, _setCompanyData] = useState([]);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [activeRecordData, setActiveRecordData] = useState<any>(undefined);
  const [tableColumn, setTableColumn] = useState([]);
  const [activeInlineEdit, setActiveInlineEdit] = useState(undefined);
  const [filter, setFilter] = useState({});
  const { setSearchProgress, search } = useGlobalSearch();
  const { toast } = useToast();

  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();
  const [deleteId, setDeleteId] = useState(undefined);

  const [sortBy, setSortBy] = useState("cts");
  const [orderBy, setOrderBy] = useState(false);

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  const [errorMessage, setErrorMessage] = useState<any>({
    title: "",
    message: "",
    open: false,
  });

  const closeErrorMessage = () => {
    setErrorMessage({
      title: "Error",
      message: "",
      open: false,
    });
  };

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingList({ searchText: "" });
      await loadingRetailerDropdownList();
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
    return () => {
      closeErrorMessage();
    };
  }, []);

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        if (deleteResponse.data) {
          await loadingList({});
          toast({
            title: "Success",
            variant: "success",
            description:
              deleteResponse?.data.result.metadata.message ??
              deleteResponse?.data?.status?.msg ??
              deleteResponse?.data?.status?.message,
          });
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        sku_map_id: deleteId,
        company_id: company_id,
      },
      company_id: company_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        setPaginationData((prev) => ({ ...prev, page: 1 }));
        await loadingList({ page: 1 });
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];
  const onAction = async ({ action, data }: any) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.sku_map_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this SKU mapping?",
      });
      await loadingList({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);

      closeErrorMessage();
    }
  }, [formPopupOpen]);

  const sortingHandler = async (prop: string) => {
    const tempSortBy = prop;
    const tempOrderBy = sortBy == prop ? !orderBy : true;
    console.log({ tempSortBy, tempOrderBy });
    setProcess(true);
    setSortBy(tempSortBy);
    setOrderBy(tempOrderBy);
    await loadingList({
      sortByProp: tempSortBy,
      orderByProp: tempOrderBy,
    });
    setProcess(false);
  };

  const loadingList = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
    orderByProp,
    sortByProp,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
    orderByProp?: any;
    sortByProp?: any;
  }) => {
    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }

    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const tempSortBy = sortByProp ?? sortBy;
    const tempOrderBy = orderByProp ?? orderBy;

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: tempSortBy,
            order_by: tempOrderBy,
          },
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    if (data) {
      const listData = data.result.metadata[API_SERVICE.key];
      setListData(listData);
      const retailerIdList = listData?.map((item: any) => item.retailer_id);

      await loadingRetailer({ retailer_id: retailerIdList || [] });

      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    return { data, error };
  };

  const loadingRetailer = async (retailerProp: any) => {
    const payload = {
      data: {
        filter_by: {
          ...retailerProp,
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      retailers.get,
      "post",
      payload,
    );

    if (data) {
      setRetailerList(data.result.metadata?.[retailers.key]);
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };
  const loadingRetailerDropdownList = async () => {
    const payload = {
      data: {
        filter_by: {
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      retailers.get,
      "post",
      payload,
    );

    if (data) {
      setRetailerDropdownList(data.result.metadata?.[retailers.key]);
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };
  const searchErpSku = async (key: string): Promise<any> => {
    const payload = {
      data: {
        key: key,
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      erpSku.get,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data: data?.result?.metadata?.[erpSku?.key], error };
  };

  const onFormSubmit = async (prop: any): Promise<any> => {
    let formData = {};
    // const method = activeRecordData ? "patch" : "post";
    // const url = activeRecordData ? API_SERVICE.update : API_SERVICE.post;
    if (activeRecordData) {
      formData = {
        po_sku: prop.po_sku,
        erp_sku: prop.erp_sku,
        product_name: prop.product_name,
        retailer_id: prop.retailer_id,
        erp_sku_code: prop.erp_sku_code,
        erp_sku_name: prop.erp_sku_name,
        erp_uom: prop.erp_uom,
        sku_map_id: activeRecordData?.sku_map_id || undefined,
      };
    } else {
      formData = {
        po_sku: prop.po_sku,
        erp_sku: prop.erp_sku,
        product_name: prop.product_name,
        retailer_id: prop.retailer_id,
        erp_sku_code: prop.erp_sku_code,
        erp_sku_name: prop.erp_sku_name,
        erp_uom: prop.erp_uom,
      };
    }
    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    console.log({ payload });

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (error) {
      // toast({
      //   title: "Error",
      //   variant: "destructive",
      //   description: error.status?.message || message,
      // });
      setErrorMessage({
        title: "Error",
        message: error.status?.message || message,
        open: true,
      });
    }

    if (data) {
      await loadingList({});
      toast({
        title: "Success",
        variant: "success",
        description:
          data.result.metadata.message ??
          data?.status?.msg ??
          data?.status?.message,
      });
    }

    if (error) {
      setProcess(false);
    }
    return { data, error };
  };

  const onInlineUpdate = async (prop: any): Promise<any> => {
    console.log({ prop });
    setProcess(true);
    let formData = {};
    // formData = {
    //   po_sku: prop.po_sku,
    //   erp_sku: prop.erp_sku,
    //   product_name: prop.product_name,
    //   retailer_id: prop.retailer_id,
    // };
    formData = {
      po_sku: prop.po_sku,
      erp_sku: prop.erp_sku,
      product_name: prop.product_name,
      retailer_id: prop.retailer_id,
      erp_sku_code: prop.erp_sku_code,
      erp_sku_name: prop.erp_sku_name,
      erp_uom: prop?.erp_uom || undefined,
      sku_map_id: prop?.sku_map_id || undefined,
    };
    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error /* statusCode */ } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      await loadingList({});
    }

    setProcess(false);
    return { data, error };
  };

  // loadingList;
  const filterRequest = async () => {
    setProcess(true);
    await loadingList({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingList({ filterReset: true });
    setProcess(false);
  };

  const exportDocument = async () => {
    setProcess(true);
    const payload = {
      data: {},
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message /* error, message, statusCode */ } =
      await apiServices<any>(API_SERVICE.export, "post", payload);

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    if (data) {
      const listData = data.result.metadata.export_path;
      const url = await getFileUrl(listData);

      downloadImage(url);
    }
    setProcess(false);
  };

  function downloadImage(url: string) {
    // Create an anchor element
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "downloaded-document";

    // Append the anchor to the document body
    document.body.appendChild(anchor);

    // Programmatically click the anchor to trigger the download
    anchor.click();

    // Remove the anchor from the document
    document.body.removeChild(anchor);
  }

  const onRunDocument = async (prop: any): Promise<any> => {
    setProcess(true);

    const payload = {
      data: { file_path: prop },
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message /* statusCode */ } = await apiServices<any>(
      API_SERVICE.import,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const importDocument = async (prop: any) => {
    if (!prop) return;
    setProcess(true);
    const payload = new FormData();

    payload.append("files", prop);

    const { data, error, message /* statusCode */ } = await apiServices<any>(
      documentsUpload.post + "/" + company_id,
      "post",
      payload,
    );

    if (data) {
      const runDocument = await onRunDocument(data.metadata.file_path);

      await loadingList({});
      toast({
        title: "Success",
        variant: "success",
        description: runDocument?.data?.result?.metadata?.message,
      });
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    setProcess(false);
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    companyData,
    retailerList,
    searchErpSku,
    onInlineUpdate,
    activeInlineEdit,
    setActiveInlineEdit,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    retailerDropdownList,
    exportDocument,
    importDocument,
    errorMessage,
    closeErrorMessage,
    sortingHandler,
    sortBy,
    orderBy,
  };
};
